import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { toast } from 'sonner';
import { 
  History,
  Download,
  Loader2,
  FileText,
  FilePlus,
  Check,
  Clock,
  Eye,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileCheck,
  AlertCircle,
  CheckCircle,
  XCircle,
  MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  review: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  final: { label: 'Final', color: 'bg-emerald-100 text-emerald-800', icon: FileCheck },
  superseded: { label: 'Superseded', color: 'bg-gray-100 text-gray-500', icon: XCircle }
};

export default function ReportVersionHistory({ reportId, onVersionSelect }) {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [versionData, setVersionData] = useState(null);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [addingNotes, setAddingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');

  useEffect(() => {
    if (reportId) {
      fetchVersions();
    }
  }, [reportId]);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/compliance/reports/${reportId}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersionData(response.data);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (versionId, versionNumber, format) => {
    try {
      const response = await axios.get(
        `${API}/compliance/reports/${reportId}/versions/${versionId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_v${versionNumber}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Version ${versionNumber} downloaded`);
    } catch (err) {
      toast.error('Failed to download version');
    }
  };

  const handleStatusChange = async (versionId, newStatus) => {
    setUpdatingStatus(versionId);
    try {
      await axios.put(
        `${API}/compliance/reports/${reportId}/versions/${versionId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchVersions();
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteVersion = async (versionId, versionNumber) => {
    if (!confirm(`Delete version ${versionNumber}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(
        `${API}/compliance/reports/${reportId}/versions/${versionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchVersions();
      toast.success('Version deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete version');
    }
  };

  const handleSaveNotes = async (versionId) => {
    try {
      await axios.post(
        `${API}/compliance/reports/${reportId}/versions/${versionId}/notes`,
        { notes: notesText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchVersions();
      setAddingNotes(null);
      setNotesText('');
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  if (!versionData || versionData.versions.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-muted-foreground">No versions generated yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Generate a report to create the first version
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-5 w-5 text-accent" />
            Version History
          </CardTitle>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{versionData.total_versions} versions</span>
            {versionData.latest_final_version && (
              <Badge variant="outline" className="bg-emerald-50">
                Final: v{versionData.latest_final_version}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {versionData.versions.map((version) => {
            const statusConfig = STATUS_CONFIG[version.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedVersion === version.id;
            
            return (
              <div
                key={version.id}
                className={`border rounded-lg transition-all ${
                  version.status === 'final' ? 'border-emerald-200 bg-emerald-50/30' : ''
                }`}
              >
                {/* Version Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedVersion(isExpanded ? null : version.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="font-medium">v{version.version_number}</span>
                    </div>
                    
                    <Badge className={statusConfig.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                    
                    <Badge variant="outline" className="text-xs">
                      {version.format.toUpperCase()}
                    </Badge>
                    
                    {version.has_watermark && (
                      <Badge variant="outline" className="text-xs bg-amber-50">
                        {version.watermark_text}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(version.generated_at)}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(version.id, version.version_number, version.format);
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t bg-gray-50/50">
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      <div>
                        <span className="text-muted-foreground">Generated by:</span>
                        <span className="ml-2">{version.generated_by_name || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">File size:</span>
                        <span className="ml-2">{formatFileSize(version.file_size)}</span>
                      </div>
                      {version.approved_by && (
                        <>
                          <div>
                            <span className="text-muted-foreground">Approved by:</span>
                            <span className="ml-2">{version.approved_by}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Approved at:</span>
                            <span className="ml-2">{formatDate(version.approved_at)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Notes */}
                    {version.notes && (
                      <div className="mb-4 p-2 bg-white rounded border">
                        <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                          <MessageSquare className="h-3 w-3" /> Notes
                        </span>
                        <p className="text-sm">{version.notes}</p>
                      </div>
                    )}
                    
                    {/* Add Notes */}
                    {addingNotes === version.id ? (
                      <div className="mb-4 space-y-2">
                        <Textarea
                          value={notesText}
                          onChange={(e) => setNotesText(e.target.value)}
                          placeholder="Add notes about this version..."
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleSaveNotes(version.id)}>
                            Save Notes
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => {
                            setAddingNotes(null);
                            setNotesText('');
                          }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {/* Status Change */}
                        <Select
                          value={version.status}
                          onValueChange={(value) => handleStatusChange(version.id, value)}
                          disabled={updatingStatus === version.id}
                        >
                          <SelectTrigger className="w-36 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="review">In Review</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="final">Mark as Final</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {!version.notes && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAddingNotes(version.id);
                              setNotesText(version.notes || '');
                            }}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Add Notes
                          </Button>
                        )}
                      </div>
                      
                      {/* Delete (only for draft/superseded) */}
                      {['draft', 'superseded'].includes(version.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteVersion(version.id, version.version_number)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
